import { Typography, CircularProgress, Box } from "@mui/material";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import CustomTable from "global/components/CustomTable/CustomTable";
import urls from "global/constants/UrlConstants";
import {
  convertESTtoUserLocalDateAndTime,
  handleErrorMessage,
  isTruthy,
} from "helpers/methods";
import { useState, useEffect } from "react";
import {
  fetchFailedNewUserActions,
  fetchFailedResponseNew,
  fetchFailedUserActions,
  fetchFailedUserActionsCounts,
  fetchUserActions,
  fetchUserActionsCounts,
} from "screens/Campaigns/CampaignService";

// Metric Mapping
export const metricLabelsForUserActions: { [key: string]: number } = {
  Open: 5,
  Click: 6,
  Unsubscribe: 7,
  "Soft Bounce": 9,
  "Hard Bounce": 10,
  Spam: 3,
};

const getTableHeaders = (forAction: string) => {
  switch (forAction) {
    case "Open":
      return [
        { name: "Contact", field: "contact", align: "left" },
        { name: "Opened On", field: "openedOn", align: "left" },
      ];
    case "Click":
      return [
        { name: "Contact", field: "contact", align: "center" },
        { name: "Clicked On", field: "clickedOn", align: "center" },
        { name: "Clicked From", field: "clickedFrom", align: "center" },
        { name: "Clicked Device", field: "clickedDevice", align: "center" },
        { name: "Clicked URLs", field: "urls", align: "center" },
      ];
    case "Unsubscribe":
      return [
        { name: "Contact", field: "contact", align: "center" },
        { name: "Unsubscribed On", field: "unsubscribedOn", align: "center" },
        {
          name: "Unsubscribed From",
          field: "unsubscribedFrom",
          align: "center",
        },
        {
          name: "Unsubscribed Device",
          field: "unsubscribedDevice",
          align: "center",
        },
      ];
    case "Hard Bounce":
    case "Soft Bounce":
    case "Failed":
      return [
        { name: "Contact", field: "contact", align: "left" },
        { name: "Type", field: "type", align: "left" },
        { name: "Reason", field: "reason", align: "left" },
      ];
    default:
      return [{ name: "Contact", field: "contact", align: "left" }];
  }
};

interface CustomProps {
  for: string;
  open: boolean;
  setOpen: Function;
  campaignId: number;
}

const UserActionModal = (props: CustomProps) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    page: 1,
    rowsPerPage: 5,
  });

  useEffect(() => {
    if (props.open) {
      fetchData();
    }
  }, [props.open, props.for, props.campaignId, filters]);

  // API Endpoints Mapping

  const getApiUrl = () => {
    const actionType = props.for;
    const campaignId = props.campaignId;
    const page = filters.page;
    const rowsPerPage = filters.rowsPerPage;
    switch (actionType) {
      case "Open":
      case "Click":
      case "Unsubscribe": {
        return {
          counts: fetchUserActionsCounts(
            campaignId,
            metricLabelsForUserActions[actionType]
          ),
          data: fetchUserActions(
            campaignId,
            metricLabelsForUserActions[actionType],
            page,
            rowsPerPage
          ),
        };
      }
      case "Failed":
        return {
          counts: fetchFailedResponseNew(campaignId),
          data: fetchFailedNewUserActions(
            campaignId,
            metricLabelsForUserActions[actionType],
            page,
            rowsPerPage
          ),
        };
      default: {
        return {
          counts: fetchFailedUserActionsCounts(
            campaignId,
            metricLabelsForUserActions[actionType]
          ),
          data: fetchFailedUserActions(
            campaignId,
            metricLabelsForUserActions[actionType],
            page,
            rowsPerPage
          ),
        };
      }
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const actionType = props.for;
      const apis = getApiUrl();
      const [count, response] = await Promise.all([apis.counts, apis.data]);
      setRows(getTableRows(response, actionType));
      setCount(count);
    } catch (err: any) {
      handleErrorMessage(err);
    } finally {
      setLoading(false);
    }
  };

  const getResponse = (responseId: number) => {
    switch (responseId) {
      case 0:
        return "Abuse";
      case 1:
        return "Requested";
      case 2:
        return "Sent";
      case 3:
        return "Spam";
      case 4:
        return "Dropped";
      case 5:
        return "Failed";
      case 6:
        return "Success";
      case 7:
        return "Unsubscribed";
      case 8:
        return "Invalid";
      default:
        return "-";
    }
  };

  const getTableRows = (data: any[], actionType: string) => {
    return data.map((item) => {
      switch (actionType) {
        case "Open":
          return {
            contact: item.contactEmail,
            openedOn: isTruthy(item.actionDetails?.actionTimeLines[0])
              ? convertESTtoUserLocalDateAndTime(
                  item.actionDetails?.actionTimeLines[0],
                  "MMMM D, YYYY [at] h:mm A"
                )
              : "N/A",
          };
        case "Click":
          return {
            contact: item.contactEmail,
            clickedOn: isTruthy(item.actionDetails?.actionTimeLines[0])
              ? convertESTtoUserLocalDateAndTime(
                  item.actionDetails?.actionTimeLines[0],
                  "MMMM D, YYYY [at] h:mm A"
                )
              : "N/A",
            clickedFrom: `${item.actionDetails.city || "Unknown"}, ${
              item.actionDetails.country || "Unknown"
            }`,
            clickedDevice: item.actionDetails?.userAgent || "Unknown",
            urls: item.actionDetails?.urls?.join(", ") || "N/A",
          };
        case "Unsubscribe":
          return {
            contact: item.contactEmail,
            unsubscribedOn: isTruthy(item.actionDetails?.actionTimeLines[0])
              ? convertESTtoUserLocalDateAndTime(
                  item.actionDetails?.actionTimeLines[0],
                  "MMMM D, YYYY [at] h:mm A"
                )
              : "N/A",
            unsubscribedFrom: `${item.actionDetails.city || "Unknown"}, ${
              item.actionDetails.country || "Unknown"
            }`,
            unsubscribedDevice: item.actionDetails?.userAgent || "Unknown",
          };
        case "Hard Bounce":
        case "Soft Bounce":
        case "Failed":
          return {
            contact: item.contactEmail,
            reason: item.reason || "N/A",
            type: (
              <Typography variant="body2">
                {getResponse(item.responseId)}
              </Typography>
            ),
          };
        default:
          return { contact: item.contactEmail };
      }
    });
  };

  const handleDialogClose = () => {
    props.setOpen(false);
    setFilters({
      page: 1,
      rowsPerPage: 5,
    });
  };

  return (
    <CustomDialog
      handleDialogClose={handleDialogClose}
      isDialogOpen={props.open}
      title={
        <Typography variant="h4" color="#7551D0">
          {props.for} Statistics
        </Typography>
      }
      body={
        loading ? (
          <Box style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <CustomTable
            isLoading={false}
            headers={getTableHeaders(props.for)}
            rows={rows}
            page={filters.page}
            rowsPerPage={filters.rowsPerPage}
            count={count ?? 0}
            showJumpToPageNumber={false}
            onPageChange={(event: any, newPage: number) => {
              setFilters({
                ...filters,
                page: newPage,
              });
            }}
            onRowsPerPageChange={(number: number) => {
              setFilters({
                ...filters,
                page: 1,
                rowsPerPage: number,
              });
            }}
            rowsPerPageOptions={[5, 10, 25, 50, 75, 100]}
          />
        )
      }
      maxWidth="lg"
      closable
      closeButtonVisibility
    />
  );
};

export default UserActionModal;
